import { useSimpleShoppingCart } from "@with-nx/ecommerce";
import {
  AnalyticsHelper,
  Currency,
  fetchCMSClientsSSR,
  fetchCMSPartnersSSR,
  fetchScenesSSR,
  Formatter,
  getLicensorImage,
  GoogleSchema,
  requestShowBrandOnlyShowSSR,
  requestVariantSSR,
  usePricing,
  useSearchPricing,
} from "@with-nx/hooks-n-helpers";
import { useGatedShowContent } from "@with-nx/hooks-n-helpers";
import { useServerRegion } from "@with-nx/region";
import {
  DesignedButton,
  DesignedCard,
  DesignedIcon,
  ReusableSection,
  Rule,
} from "@with-nx/simple-ui/atoms";
import {
  CatalogLayout,
  CatalogLayoutWait,
  ClientDetails,
  GridStepScroller,
  ReusablePricingBox,
  ReusableSimpleHeroSection,
} from "@with-nx/simple-ui/organisms";
import {
  ClientStaticData,
  PartnerStaticData,
  ShowDataStatic,
  ShowRedirectionStaticData,
} from "@with-nx/static";
import { Col, Row } from "antd";
import { useRouter } from "next/router";
import { Box } from "simple-effing-primitive-layout";

import { ReusableScenicProjectionsFeatures } from "../../../../../components/ReusableScenicProjectionsFeatures";
import { ScenePreviews } from "../../../../../components/w/scene-previews/ScenePreviews";
import { ShowsTab } from "../../../../../components/w/shows/ShowsTab";
import { useEffect } from "react";

export const getServerSideProps = async (context) => {
  const { resolvedUrl } = context;
  const { variant } = context.params;

  const showVariant = !ShowDataStatic.isShowsEmpty()
    ? ShowDataStatic.getShowBySlug(variant) ||
      (await requestVariantSSR(variant))
    : await requestVariantSSR(variant);

  const scenicProjection =
    showVariant?.scenicProjections?.find((i) => i.isDefault) || null;

  const [scenes, showOnly] = await (async () => {
    try {
      const [scenes, showOnly] = await Promise.all([
        fetchScenesSSR({
          slug: variant,
          id: scenicProjection?.id,
        }),
        requestShowBrandOnlyShowSSR(showVariant?.showBrand?.slug),
      ]);

      return [scenes, showOnly];
    } catch {
      return [[], { only: true }];
    }
  })();

  const clients = !ClientStaticData.isClientsEmpty()
    ? ClientStaticData.getClientByShowVariantTag(showVariant?.id?.toString()) ||
      (await fetchCMSClientsSSR({
        filter: {
          showVariantTags: [showVariant?.id?.toString()],
        },
      }))
    : await fetchCMSClientsSSR({
        filter: {
          showVariantTags: [showVariant?.id?.toString()],
        },
      });
  const client =
    clients?.find((item) => item?.clientType === "Designer") || null;
  const partners = !PartnerStaticData.isPartnersEmpty()
    ? PartnerStaticData.getAllPartners() || (await fetchCMSPartnersSSR({}))
    : await fetchCMSPartnersSSR({});

  const showRedirection =
    ShowRedirectionStaticData.getRedirectionByShowSlug(variant);

  return {
    props: {
      resolvedUrl,

      showVariant,
      scenicProjection,
      scenes,
      showOnly,

      showRedirection: showRedirection || null,

      client,
      partners: partners || [],
    },
  };
};

export default function Page({
  resolvedUrl,
  showVariant,
  scenicProjection,
  scenes,
  showOnly,
  client,
  partners,
  showRedirection,
}) {
  const router = useRouter();
  const region = useServerRegion();

  const showSlug = router.query?.brand as string;
  const showId = showVariant?.id;
  const gate = useGatedShowContent(showSlug, showId);
  gate.register();

  useEffect(() => {
    AnalyticsHelper.collect(showVariant?.id);
  }, [showVariant?.id]);

  const licenses = useSearchPricing(
    {
      search: showVariant?.title,
      type: "SCENIC_PROJECTION_PACKAGE",
      scenicProjectionId: scenicProjection?.id,
      region: region?.id,
    },
    Boolean(scenicProjection?.id) && Boolean(region?.id)
  );

  const isJunior =
    showVariant?.title?.toLowerCase()?.endsWith("youth edition") ||
    showVariant?.title?.toLowerCase()?.endsWith("kids") ||
    showVariant?.title?.toLowerCase()?.endsWith("jr");

  const pricing = usePricing(
    {
      type: "SCENIC_PROJECTION_PACKAGE",
      variation: isJunior ? "JUNIOR" : "REGULAR",
      region: region?.id,
    },
    Boolean(showVariant?.id) && Boolean(region?.id)
  );

  const basket = useSimpleShoppingCart();
  const licensor = licenses?.pricing?.[0]?.licensors?.[0]?.licensor;

  const _licensor = getLicensorImage(
    partners,
    licensor?.name,
    licensor?.abbreviation
  );

  if (!showVariant?.id) {
    return null;
  }

  const curtainWarmer = scenicProjection?.attachments?.find(
    (i) => i.name === "curtain-warmer"
  );

  let shouldHideLogo =
    (!showVariant?.scenicProjections.length && !showVariant?.choreoGuide) ||
    !licenses?.pricing?.length;

  if (showVariant?.forceShowLogo) {
    shouldHideLogo = false;
  }

  const redirectionUrl = showRedirection?.regions?.[region?.id || "1"];
  const redirectionLogo = showRedirection?.logo;
  const redirectionColor = showRedirection?.color;

  const waitlist = redirectionUrl
    ? false
    : !scenicProjection?.id ||
      (!licenses?.pricing?.length && !licenses.loading);

  const until = [
    waitlist ? true : !licenses.loading,
    waitlist ? true : !gate.loading,
  ];

  const PricingBox = ({ waitlist }: { waitlist?: boolean }) => {
    const PricingBoxContainer = ({
      children,
    }: {
      children: JSX.Element | JSX.Element[];
    }) =>
      waitlist ? (
        <>{children}</>
      ) : (
        <ReusableSection>
          <div className="container">
            <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
              Scenic Projections License Options
            </Rule>
            {children}
          </div>
        </ReusableSection>
      );

    return !licenses?.pricing?.length ? (
      <PricingBoxContainer>
        <GridStepScroller
          xs={24}
          sm={24}
          md={waitlist ? 24 : 12}
          lg={waitlist ? 24 : 12}
          items={[
            <ReusablePricingBox
              key={0}
              cart={false}
              mode="top-left-yellow"
              title="Standard License | StagePlayer"
              span={24}
              price={Currency.format(
                parseFloat(pricing?.price),
                region?.currency.code
              )}
              description="Script-accurate scenery that comes pre-programmed in Mac or PC software."
              features={["Pre-Cued", "Rehearsal Materials"]}
              onPurchaseWithRepresentative={undefined}
              onLearnMore={undefined}
              minimal
            />,
          ]}
        />
      </PricingBoxContainer>
    ) : licenses?.pricing.length ? (
      <PricingBoxContainer>
        <Rule parse="!pl c:?font2 d:block mb:24" bottom={24}>
          Scenic Projections are script-accurate show packages that feature
          beautifully animated scenery created from the author&apos;s vision and
          intent. Every scene, setting, and magical moment in the show is
          included and is meticulously crafted to bring the show to life on your
          stage.
        </Rule>

        <GridStepScroller
          xs={24}
          sm={24}
          md={12}
          lg={12}
          items={licenses?.pricing.map((license, l) => {
            return (
              <>
                <GoogleSchema
                  data={{
                    id: String(license.id),
                    name: `${showVariant?.title} Scenic Projections - ${
                      license.variation === "PROFESSIONAL"
                        ? "Professional"
                        : "Standard"
                    } License`,
                    price: license.price,
                    currency: region?.currency.code,
                    unit: "4 weeks",
                    image: showLogo,
                  }}
                />
                <ReusablePricingBox
                  cart={basket.added(
                    license.id,
                    license.variation === "PROFESSIONAL"
                  )}
                  cypress={Formatter.slugify(license?.variation || "LICENSE")}
                  onCart={() => {
                    router.push("/checkout");
                  }}
                  key={l}
                  mode={
                    ["top-left-yellow", "top-left-grey"][l % 2] as
                      | "top-left-yellow"
                      | "top-left-grey"
                  }
                  title={(() => {
                    switch (license.variation) {
                      case "JUNIOR":
                        return "Standard License | StagePlayer";
                      case "REGULAR":
                        return "Standard License | StagePlayer";
                      case "PROFESSIONAL":
                        return "Professional License | QLab";

                      default:
                        break;
                    }
                  })()}
                  span={license?.variation === "PROFESSIONAL" ? 12 : 24}
                  price={license?.pricing?.price}
                  description={
                    license.variation === "PROFESSIONAL"
                      ? "Designed for professionals! Mapping-capable, customizable scenery for theatre organizations with advanced projection capabilities."
                      : "No programming required! All materials are delivered in StagePlayer, our easy-to-use software for Theatre-Makers by Theatre-Makers."
                  }
                  preFeaturesDescription={
                    license.variation === "PROFESSIONAL"
                      ? "The Professional License offers you the creative freedom to work with your technical team to customize the content to suit your production's specific needs."
                      : "Scenic Projections are script-accurate show packages that feature beautifully animated scenery created from the author’s vision and intent.  Every scene, setting, and magical moment in the show is included and is meticulously crafted to bring the show to life on your stage."
                  }
                  features={(() => {
                    switch (license.variation) {
                      case "JUNIOR":
                        return [
                          "Plug and Play StagePlayer software for Mac or PC",
                          "Pre-Cued and Includes Cue Sheet",
                          "Easy editing tools (including vignettes, and image effects)",
                          "Customer Support",
                        ];
                      case "PROFESSIONAL":
                        return [
                          "Stage Player Rehearsal Materials",
                          "QLab Integration",
                        ];
                      default:
                        return [
                          "Plug and Play StagePlayer software for Mac or PC",
                          "Pre-Cued and Includes Cue Sheet",
                          "Easy editing tools (including vignettes, and image effects)",
                          "Customer Support",
                        ];
                    }
                  })()}
                  onPurchaseNow={() => {
                    if (basket.authenticated) {
                      basket
                        .add(license.id, "SCENIC_PROJECTION_PACKAGE", {
                          proLicense: license.variation === "PROFESSIONAL",
                        })
                        .then(() => router.push("/checkout"));
                    } else {
                      const authenticate = () => {
                        if (!window) {
                          return;
                        }

                        const _url = new URL(
                          window?.location?.origin + "/auth"
                        );
                        _url.search = new URLSearchParams({
                          redirect: window.location.pathname,
                          action: "Continue",
                          form: "register",
                          product: license.id as string,
                          type: "SCENIC_PROJECTION_PACKAGE",
                          proLicense:
                            license.variation === "PROFESSIONAL" ? "1" : "0",
                        }).toString();
                        router.push(_url.toString());
                      };

                      authenticate();
                    }
                  }}
                  onPurchaseWithRepresentative={() => {
                    router.push(
                      `/contact/start?mode=order-with-representative&referrer=${router.asPath}`
                    );
                  }}
                  onLearnMore={() => {
                    router.push(
                      `/contact/start?mode=learn-more&referrer=${router.asPath}`
                    );
                  }}
                  popular={license?.variation === "REGULAR"}
                />
              </>
            );
          })}
        />
      </PricingBoxContainer>
    ) : (
      <Box parse="h:200 w:100% d:block" />
    );
  };

  const showLogo = shouldHideLogo
    ? undefined
    : showVariant?.attachments?.find((i) => i.name === "logo")?.blob.signedUrl;

  return (
    <CatalogLayout
      footerNoAngle
      transparentFixedNavBar
      title={
        scenicProjection?.metaTitle
          ? scenicProjection?.metaTitle
          : showVariant?.title
          ? `${showVariant?.title} Scenic Projections | Broadway Media`
          : undefined
      }
      description={
        scenicProjection?.metaDescription
          ? scenicProjection?.metaDescription
          : showVariant?.marketingDescription
      }
      resolvedUrl={resolvedUrl}
    >
      <CatalogLayoutWait until={until}>
        <ReusableSimpleHeroSection
          image={
            showVariant?.attachments?.find((i) => i.name === "image")?.blob
              .signedUrl
          }
          show={{
            name: showVariant?.title,
            image: showLogo,
            color: showVariant?.logoColor || showVariant?.showBrand?.logoColor,
          }}
          press={() => {
            router.push(`/shows/${router.query?.brand}`);
          }}
          title={showVariant?.title}
          description={showVariant?.marketingDescription}
          licensor={
            _licensor
              ? {
                  name: licensor?.name || licensor?.abbreviation,
                  image: Formatter.image(_licensor.image),
                  url: `/partners/${_licensor?.slug}`,
                }
              : undefined
          }
        />

        <div className="container">
          <ShowsTab
            waitlist={waitlist}
            empty={false}
            loading={false}
            showVariant={showVariant}
            onlyShow={showOnly.only}
            currentTab="Scenic Projections"
          />
        </div>

        {client ? (
          <ReusableSection>
            <div className="container">
              <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
                Designer
              </Rule>
              <ClientDetails
                image={Formatter.image(client.image)}
                url={`/clients/${client?.slug}`}
                name={client.title}
                description={client.description}
              />
            </div>
          </ReusableSection>
        ) : undefined}

        <ReusableSection>
          <div className="container">
            <Row gutter={[36, 18]} align={waitlist ? "middle" : undefined}>
              <Col xs={24} sm={24} md={waitlist ? 12 : 12}>
                {!licenses.loading || waitlist ? (
                  <DesignedCard
                    size="16x9"
                    embed={
                      scenicProjection?.commercialVideoUrl ||
                      "https://www.youtube.com/watch?v=ueCs6PogVx0"
                    }
                    pop={false}
                    properties={{
                      bottom: 24,
                    }}
                  />
                ) : undefined}
              </Col>
              <Col xs={24} sm={24} md={waitlist ? 12 : 12}>
                {waitlist ? (
                  <>
                    <Rule parse="!hl d:block mb:12 c:?primary" tag="span">
                      Scenic Projections Coming Soon!
                    </Rule>
                    <Rule
                      parse="!hl d:block mb:12 c:?font1"
                      css="--blue-gradient-text"
                    >
                      Let&apos;s Put On a Show!
                    </Rule>
                    <Rule parse="!pl d:block mb:24 c:?font2">
                      At Broadway Media, we're revolutionizing the performing
                      arts landscape. We innovate, create, and inspire, offering
                      a powerful suite of technology tools crafted precisely for
                      the artists of today. Currently, there are no Scenic
                      Projection packages available for this show. Feel free to
                      join the waitlist below to be notified when they become
                      available!
                    </Rule>
                  </>
                ) : redirectionUrl ? (
                  <>
                    <Rule parse="!hl c:?font1 d:block mb:12">
                      {showVariant?.title} Scenic Projections
                    </Rule>
                    <Rule
                      parse="!ps c:?font3 d:block mb:12"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {scenicProjection?.marketingDescription}
                    </Rule>
                    <Box parse="d:flex a:center fw:wrap mt:24">
                      <Box
                        parse="d:flex a:center br:5 pa:12"
                        press={() => {
                          window.open(redirectionUrl, "_blank");
                        }}
                        style={{ gap: 12 }}
                        color={redirectionColor}
                      >
                        <img
                          style={{
                            width: "auto",
                            height: 24,
                            pointerEvents: "none",
                          }}
                          src={redirectionLogo}
                        />
                        <Box parse="w:1 h:24" color="white" opacity={0.1} />
                        <Rule parse="!lm c:?font1">License Show</Rule>
                        <DesignedIcon color="white" name="circle_arrow_right" />
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Rule parse="!hl c:?font1 d:block mb:12">
                      {showVariant?.title} Scenic Projections
                    </Rule>
                    <Rule
                      parse="!ps c:?font3 d:block mb:12"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {scenicProjection?.marketingDescription}
                    </Rule>
                    <Box parse="d:flex a:center fw:wrap mt:24">
                      <DesignedButton
                        label="Add to Cart"
                        icon="cart"
                        properties={{ right: 12, bottom: 12 }}
                        href="#licenses"
                      />
                      <DesignedButton
                        label="Request Proposal"
                        properties={{ right: 12, bottom: 12 }}
                        theme="tetriary"
                        press={() => {
                          router.push(
                            `/contact/start?mode=order-with-representative&referrer=${router.asPath}`
                          );
                        }}
                      />
                    </Box>
                    {region.id === 1 ? (
                      <Box parse="mt:12">
                        <Rule parse="!ps c:?font2 d:block">
                          Prefer to submit an order form?{" "}
                          <a
                            target="_blank"
                            href="/legal/documents/order-form"
                            style={{
                              color: "var(--primary)",
                              textDecoration: "underline",
                            }}
                          >
                            Download Order Form
                          </a>
                        </Rule>
                      </Box>
                    ) : undefined}
                  </>
                )}
              </Col>
            </Row>
          </div>
        </ReusableSection>

        <ScenePreviews
          showTitle={showVariant?.title}
          customTitle={
            <>
              <b>{showVariant?.title} Scenic Projections</b>
              {" | Scene Previews"}
            </>
          }
          scenes={
            showRedirection ? scenes : !licenses?.pricing?.length ? [] : scenes
          }
          curtainWarmer={curtainWarmer}
          showId={showId}
          redirectionUrl={redirectionUrl}
        />

        <div id="licenses" />

        {waitlist || redirectionUrl ? undefined : <PricingBox />}

        <ReusableScenicProjectionsFeatures />
      </CatalogLayoutWait>
    </CatalogLayout>
  );
}
